import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { LogOut, RefreshCw, Loader2, Lock, Phone, Mail, StickyNote, CalendarDays } from "lucide-react";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/api";

const waReminderLink = (b) => {
    const digits = (b.phone || "").replace(/\D/g, "");
    const intl = digits.startsWith("39") ? digits : `39${digits}`;
    const when = format(new Date(`${b.date}T00:00:00`), "EEEE d MMMM", { locale: it });
    const text = `Ciao ${b.first_name}, ti ricordo l'appuntamento di ${when} alle ${b.time} per il tuo ${b.treatment_name}. Lo studio è in Via Marsala 28, Bologna. A presto! Sara`;
    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
};

const STATUS_STYLE = {
    pending: { label: "In attesa", cls: "bg-beige/40 text-bark" },
    confirmed: { label: "Confermata", cls: "bg-sage-light text-sage-dark" },
    cancelled: { label: "Annullata", cls: "bg-red-50 text-red-700" },
};

const Admin = () => {
    const [user, setUser] = useState(null); // null = checking, false = logged out
    const [credentials, setCredentials] = useState({ email: "", password: "" });
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState("");
    const [calendar, setCalendar] = useState(null);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (searchParams.get("calendar") === "connected") {
            toast.success("Google Calendar collegato: gli impegni del tuo calendario bloccheranno gli orari.");
        }
    }, [searchParams]);

    const loadBookings = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter !== "all" ? { status: filter } : {};
            const res = await api.get("/admin/bookings", { params });
            setBookings(res.data.bookings);
        } catch {
            toast.error("Impossibile caricare le prenotazioni");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        api.get("/auth/me")
            .then((res) => setUser(res.data))
            .catch(() => setUser(false));
    }, []);

    useEffect(() => {
        if (user) {
            loadBookings();
            api.get("/oauth/calendar/status").then((res) => setCalendar(res.data)).catch(() => {});
        }
    }, [user, loadBookings]);

    const login = async (e) => {
        e.preventDefault();
        setLoginError("");
        setLoggingIn(true);
        try {
            const res = await api.post("/auth/login", credentials);
            setUser(res.data);
        } catch (err) {
            setLoginError(formatApiError(err.response?.data?.detail, "Accesso non riuscito"));
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        setUser(false);
    };

    const updateStatus = async (bookingId, status) => {
        setUpdating(bookingId);
        try {
            await api.patch(`/admin/bookings/${bookingId}`, { status });
            toast.success(status === "confirmed" ? "Prenotazione confermata, email inviata al cliente" : "Prenotazione annullata, email inviata al cliente");
            await loadBookings();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUpdating("");
        }
    };

    /* ---------- Login ---------- */
    if (!user) {
        return (
            <div className="flex min-h-[80vh] items-center justify-center px-5 pt-24" data-testid="admin-login-page">
                <SEO title="Area riservata | Studio Olistico Bologna" description="Area riservata dello studio." />
                <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-border bg-card p-8 shadow-[0_8px_40px_rgba(67,46,30,0.08)]">
                    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage-light text-sage">
                        <Lock className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <h1 className="mt-5 text-center font-serif text-2xl text-ink">Area riservata</h1>
                    <p className="mt-1 text-center text-xs text-fog">Gestione prenotazioni dello studio</p>
                    <div className="mt-7 space-y-4">
                        <div>
                            <label htmlFor="admin-email" className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-clay">Email</label>
                            <Input id="admin-email" type="email" data-testid="admin-login-email-input" value={credentials.email} onChange={(e) => setCredentials((c) => ({ ...c, email: e.target.value }))} className="rounded-xl border-border" required />
                        </div>
                        <div>
                            <label htmlFor="admin-password" className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-clay">Password</label>
                            <Input id="admin-password" type="password" data-testid="admin-login-password-input" value={credentials.password} onChange={(e) => setCredentials((c) => ({ ...c, password: e.target.value }))} className="rounded-xl border-border" required />
                        </div>
                        {loginError && (
                            <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-700" data-testid="admin-login-error">{loginError}</p>
                        )}
                        <button
                            type="submit"
                            data-testid="admin-login-submit-button"
                            disabled={loggingIn}
                            className="flex w-full items-center justify-center gap-2 rounded-full bg-sage py-3.5 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark disabled:opacity-50"
                        >
                            {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
                            Accedi
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    /* ---------- Dashboard ---------- */
    return (
        <div className="mx-auto max-w-6xl px-5 pb-28 pt-32 md:px-8" data-testid="admin-dashboard">
            <SEO title="Gestione prenotazioni | Area riservata" description="Area riservata dello studio." />
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-sage">Area riservata</p>
                    <h1 className="mt-2 font-serif text-3xl font-light text-ink md:text-4xl">Prenotazioni</h1>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        data-testid="admin-refresh-button"
                        onClick={loadBookings}
                        className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-clay transition-colors hover:border-sage hover:text-sage"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Aggiorna
                    </button>
                    <button
                        type="button"
                        data-testid="admin-logout-button"
                        onClick={logout}
                        className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-clay transition-colors hover:border-red-300 hover:text-red-600"
                    >
                        <LogOut className="h-4 w-4" strokeWidth={1.5} /> Esci
                    </button>
                </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-card p-5" data-testid="calendar-sync-card">
                <div className="flex items-center gap-3">
                    <CalendarDays className="h-5 w-5 text-sage" strokeWidth={1.5} />
                    <div>
                        <p className="text-sm font-medium text-ink">Google Calendar</p>
                        <p className="text-xs text-fog">
                            {calendar?.connected
                                ? `Collegato (${calendar.email}): gli impegni bloccano gli orari e le conferme creano eventi`
                                : calendar?.configured
                                  ? "Non collegato: collega il calendario per sincronizzare la disponibilità"
                                  : "Credenziali Google in fase di configurazione"}
                        </p>
                    </div>
                </div>
                {calendar?.connected ? (
                    <span className="inline-flex items-center gap-2 rounded-full bg-sage-light px-4 py-2 text-xs font-semibold uppercase tracking-widest text-sage-dark" data-testid="calendar-connected-badge">
                        <span className="h-2 w-2 rounded-full bg-sage" /> Attivo
                    </span>
                ) : (
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/oauth/calendar/login`}
                        data-testid="calendar-connect-button"
                        className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors ${calendar?.configured ? "bg-sage text-cream hover:bg-sage-dark" : "cursor-not-allowed bg-border text-fog"}`}
                    >
                        Collega Google Calendar
                    </a>
                )}
            </div>

            <div className="mt-8 flex gap-2" data-testid="admin-filters">
                {[
                    { id: "all", label: "Tutte" },
                    { id: "pending", label: "In attesa" },
                    { id: "confirmed", label: "Confermate" },
                    { id: "cancelled", label: "Annullate" },
                ].map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        data-testid={`admin-filter-${f.id}`}
                        onClick={() => setFilter(f.id)}
                        className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
                            filter === f.id ? "bg-sage text-cream" : "bg-sand text-clay hover:bg-beige/50"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="mt-8 space-y-4" data-testid="admin-bookings-list">
                {loading && bookings.length === 0 ? (
                    <p className="flex items-center gap-2 text-sm text-fog"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</p>
                ) : bookings.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="admin-empty-state">
                        <p className="font-serif text-xl text-ink">Nessuna prenotazione</p>
                        <p className="mt-2 text-sm text-fog">Le nuove richieste appariranno qui e nella tua email.</p>
                    </div>
                ) : (
                    bookings.map((b) => (
                        <div key={b.booking_id} className="rounded-2xl border border-border bg-card p-6 shadow-[0_4px_20px_rgba(67,46,30,0.04)]" data-testid={`booking-row-${b.booking_id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <p className="font-serif text-xl text-ink">
                                            {format(new Date(`${b.date}T00:00:00`), "EEEE d MMMM yyyy", { locale: it })} · {b.time}
                                        </p>
                                        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${STATUS_STYLE[b.status]?.cls || ""}`} data-testid={`booking-status-${b.booking_id}`}>
                                            {STATUS_STYLE[b.status]?.label || b.status}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-sm font-medium text-sage-dark">{b.treatment_name}{b.price ? ` · ${b.price}` : ""}</p>
                                    <p className="mt-2 text-sm text-clay">
                                        {b.first_name} {b.last_name} · <span className="text-fog">{b.code}</span>
                                    </p>
                                    {b.sessions && b.sessions.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`booking-sessions-${b.booking_id}`}>
                                            {b.sessions.map((s, i) => (
                                                <span key={i} className="rounded-full bg-sand px-3 py-1 text-xs text-clay">
                                                    {i + 1}. {format(new Date(`${s.date}T00:00:00`), "d MMM", { locale: it })} · {s.time}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-fog">
                                        <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" strokeWidth={1.5} />{b.phone}</span>
                                        <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" strokeWidth={1.5} />{b.email}</span>
                                        {b.notes && <span className="inline-flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5" strokeWidth={1.5} />{b.notes}</span>}
                                    </div>
                                </div>
                                {b.status === "confirmed" && (
                                    <a
                                        href={waReminderLink(b)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`booking-wa-${b.booking_id}`}
                                        className="rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-white transition-opacity hover:opacity-90"
                                    >
                                        Promemoria WhatsApp
                                    </a>
                                )}
                                {b.status === "pending" && (
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            data-testid={`booking-confirm-${b.booking_id}`}
                                            disabled={updating === b.booking_id}
                                            onClick={() => updateStatus(b.booking_id, "confirmed")}
                                            className="rounded-full bg-sage px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-cream transition-colors hover:bg-sage-dark disabled:opacity-50"
                                        >
                                            Conferma
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`booking-cancel-${b.booking_id}`}
                                            disabled={updating === b.booking_id}
                                            onClick={() => updateStatus(b.booking_id, "cancelled")}
                                            className="rounded-full border border-red-200 px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                                        >
                                            Annulla
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default Admin;
